import {
  Home,
  ListTodo,
  Gift,
  Ticket,
  Gamepad2,
  TicketPlus,
  UserCheck,
  Users,
  Contact,
  Euro,
  TrendingUp,
  Settings,
  Users2,
  BarChart3,
  StickyNote,
  FolderOpen,
  Newspaper,
  MessageSquare,
  MessageCircle,
  LucideIcon,
  Calendar,
  FileCheck,
  CreditCard,
  ClipboardList,
  KanbanSquare,
  Video,
  Bell,
  History,
  Landmark,
  Mail,
  Mails,
  Wallet,
  Tag,
  Truck,
  ReceiptText,
  ShoppingBag,
  Receipt,
  CalendarDays,
  GanttChart,
  Wrench,
  Scale,
  KeyRound,
  Package,
  Trophy,
  Handshake,
  MessageSquareText,
} from "lucide-react";
import { matchPath } from "react-router";
import {
  REPRESENTATIVES_PAGE_PATH,
  REPRESENTATIVES_DIRECTORY_PAGE_PATH,
  EMPLOYEES_PAGE_PATH,
  BENEFICIARIES_PAGE_PATH,
  EXTERNAL_CONTACTS_PAGE_PATH,
  GROUPS_PAGE_PATH,
  GROUP_NEW_PATH,
  GROUP_EDIT_PATH,
  DYNAMIC_GROUP_BUILDER_BASE,
  DYNAMIC_GROUP_NEW_PATH,
  VOUCHER_CAMPAIGN_PATH,
  VOUCHER_EMPLOYEES_PATH,
  VOUCHER_TOP_UPS_PATH,
  VOUCHER_BALANCE_PATH,
  CARD_REISSUANCE_PATH,
  JOBS_PAGE_PATH,
  PARAMETER_PATH,
  PARAMETER_IDENTITE_CSE_PATH,
  PARAMETER_STUDIO_PATH,
  PARAMETER_COORDONNEES_PATH,
  PARAMETER_INFORMATIONS_PATH,
  PARAMETER_SITES_PATH,
  PARAMETER_DATA_MANAGEMENT_PATH,
  PARAMETER_WELCOME_EMAIL_PATH,
  PARAMETER_BENEFICIARIES_PATH,
  PARAMETER_PROOF_DOCUMENTS_PATH,
  PARAMETER_REPRESENTATIVE_ROLES_PATH,
  PARAMETER_DASHBOARD_LAYOUTS_PATH,
  PARAMETER_NAV_POSITION_PATH,
  PARAMETER_EMAIL_SIGNATURE_PATH,
  POLLS_PAGE_PATH,
  SURVEYS_PAGE_PATH,
  BLOG_PAGE_PATH,
  PRONOSTICS_PAGE_PATH,
  PRONOSTIC_COMPETITION_DETAIL_PATH,
  TOMBOLA_PAGE_PATH,
  ADVENT_CALENDAR_PAGE_PATH,
  ARCADE_PAGE_PATH,
  CALENDAR_PATH,
  DISCUSSIONS_PAGE_PATH,
  CHAT_PAGE_PATH,
  AUDIT_LOGS_PAGE_PATH,
  FILE_MANAGER_PATH,
  PINNED_NOTES_PATH,
  PROOF_DOCUMENTS_PAGE_PATH,
  ACCOUNTING_MOVEMENTS_PAGE_PATH,
  ACCOUNTING_SETTLEMENTS_PAGE_PATH,
  ACCOUNTING_LEDGER_PAGE_PATH,
  ACCOUNTING_STOCK_PAGE_PATH,
  ACCOUNTING_SYNTHESIS_PAGE_PATH,
  ACCOUNTING_BUDGET_PAGE_PATH,
  ACCOUNTING_SETTINGS_PAGE_PATH,
  USER_SETTINGS_PATH,
  SUPPLIERS_PAGE_PATH,
  CHECK_CAMPAIGNS_PATH,
  CHECK_EMPLOYEES_PATH,
  CHECK_CONTRIBUTIONS_PATH,
  RULES_PAGE_PATH,
  CUSTOM_FORMS_PAGE_PATH,
  TASK_MANAGER_PAGE_PATH,
  VISIO_PAGE_PATH,
  VISIO_PUBLIC_STANDALONE_PATH,
  VISIO_ROOM_STANDALONE_PATH,
  PUSH_NOTIFICATIONS_PAGE_PATH,
  OPEN_QUESTIONS_PAGE_PATH,
  LEGAL_QUESTIONS_PAGE_PATH,
  SMS_ADMIN_PAGE_PATH,
  COMMUNICATION_DISCUSSIONS_PATH,
  COMMUNICATION_NEWSLETTERS_PATH,
  COMMUNICATION_REPLIES_PATH,
  COMMUNICATION_CSE_MESSAGES_PATH,
  SUBSIDY_POLICIES_PAGE_PATH,
  SUBSIDY_LEDGER_PAGE_PATH,
  SUBSIDY_CREDITS_PAGE_PATH,
  ACTIVITY_OPERATIONS_PAGE_PATH,
  DEALS_PAGE_PATH,
  DELIVERY_MODES_PAGE_PATH,
  CHANGELOGS_PAGE_PATH,
  REFUNDS_CONFIGURATION_PATH,
  ECOM_ORDERS_PAGE_PATH,
  REFUND_REQUESTS_PAGE_PATH,
  YEATI_LOC_PATH,
  YEATI_LOC_BOOKING_DETAIL_PATH,
  YEATI_LOC_CATALOGUE_PATH,
  YEATI_LOC_COLLECTIONS_PATH,
  YEATI_LOC_RESERVATIONS_PATH,
  YEATI_LOC_PLANNING_PATH,
  YEATI_LOC_NEW_PRODUCT_PATH,
  YEATI_LOC_PRODUCT_DETAIL_PATH,
  YEATI_LOC_SETTINGS_PATH,
  CLASSIFIED_ADS_PATH,
} from "@/shared/configs/routes.config";
import type { UpsellCategoryKey } from "@/shared/types/common";
import type { PlanFeatures } from "@/shared/types/planFeatures";
import type { ReadPermissionCode, RepresentativePermissions } from "@/shared/types/representativePermissions";
import { isEffectiveFeatureEnabled } from "@/shared/utils/contentFeatureAvailability.utils";

// `requiresAll` is only honoured by the `{ feature, requiresAny }` branch of
// checkFeatureRequirement. The `never` on the other two shapes makes writing it
// there a compile error instead of a silently ignored gate.
export type RouteFeatureRequirement =
  | keyof PlanFeatures
  | { all: Array<keyof PlanFeatures>, requiresAll?: never }
  | { any: Array<keyof PlanFeatures>, requiresAll?: never }
  | {
    feature: keyof PlanFeatures
    requiresAny: Array<keyof PlanFeatures>
    /** Extra features that must *all* be on, on top of `feature`. */
    requiresAll?: Array<keyof PlanFeatures>
  };

export type RouteConfig = {
  path: string
  feature?: RouteFeatureRequirement
  requiresRepresentativeOrganization?: boolean
  readPermission?: ReadPermissionCode
  upsellCategory?: UpsellCategoryKey
  navigation?: {
    label: string
    icon: LucideIcon
    order?: number
    parent?: string
    activePaths?: string[]
  }
  search?: {
    value: string
    label: string
    category: string
    keywords: string[]
  }
};

export type ParentRouteConfig = {
  id: string
  label: string
  icon: LucideIcon
  order: number
  upsellCategory?: UpsellCategoryKey
};

export const PARENT_ROUTES: Record<string, ParentRouteConfig> = {
  users: {
    id: "users",
    label: "Utilisateurs",
    icon: Users,
    order: 20,
  },
  accounting: {
    id: "accounting",
    label: "Comptabilité",
    icon: Landmark,
    order: 35,
    upsellCategory: "accounting",
  },
  vouchers: {
    id: "vouchers",
    label: "Titres cadeaux",
    icon: Gift,
    order: 40,
  },
  checks: {
    id: "checks",
    label: "Cartes cadeaux démat.",
    icon: Wallet,
    order: 42,
    upsellCategory: "checks",
  },
  deals: {
    id: "deals",
    label: "Bons Plans",
    icon: Tag,
    order: 45,
    upsellCategory: "deals",
  },
  yeatiLoc: {
    id: "yeatiLoc",
    label: "Location",
    icon: KeyRound,
    order: 46,
    upsellCategory: "yeati_loc",
  },
  communication: {
    id: "communication",
    label: "Communication",
    icon: Mail,
    order: 82,
    upsellCategory: "communication",
  },
  refunds: {
    id: "refunds",
    label: "Remboursement",
    icon: ReceiptText,
    order: 36,
    upsellCategory: "refunds",
  },
  subsidies: {
    id: "subsidies",
    label: "Subventions",
    icon: Wallet,
    order: 34,
    upsellCategory: "subsidies",
  },
  activities: {
    id: "activities",
    label: "Vie du CSE",
    icon: CalendarDays,
    order: 83,
    upsellCategory: "activities",
  },
  tools: {
    id: "tools",
    label: "Outils",
    icon: Wrench,
    order: 84,
    upsellCategory: "tools",
  },
};

export const ROUTE_REGISTRY: Record<string, RouteConfig> = {
  dashboard: {
    path: "/",
    navigation: {
      label: "Tableau de bord",
      icon: Home,
      order: 10,
    },
    search: {
      value: "dashboard",
      label: "Tableau de bord",
      category: "Principales",
      keywords: ["tableau", "bord", "dashboard", "accueil", "home"],
    },
  },

  representatives: {
    path: REPRESENTATIVES_PAGE_PATH,
    feature: "service_representatives",
    readPermission: "service_representatives:read",
    navigation: {
      label: "Élus",
      icon: UserCheck,
      parent: "users",
    },
    search: {
      value: "representatives",
      label: "Élu(s)",
      category: "Principales",
      keywords: ["élu", "représentant", "représentants"],
    },
  },

  representativesDirectory: {
    path: REPRESENTATIVES_DIRECTORY_PAGE_PATH,
    feature: "service_representative_directory",
    readPermission: "service_representative_directory:read",
    navigation: {
      label: "Trombinoscope",
      icon: Users,
      parent: "users",
    },
    search: {
      value: "representatives-directory",
      label: "Trombinoscope",
      category: "Principales",
      keywords: ["trombinoscope", "annuaire", "elus"],
    },
  },

  employees: {
    path: EMPLOYEES_PAGE_PATH,
    feature: "service_employees",
    readPermission: "service_employees:read",
    navigation: {
      label: "Salariés",
      icon: Users,
      parent: "users",
    },
    search: {
      value: "salaries",
      label: "Salariés",
      category: "Principales",
      keywords: ["salariés", "employés", "collaborateurs"],
    },
  },

  beneficiaries: {
    path: BENEFICIARIES_PAGE_PATH,
    feature: "service_beneficiaries",
    readPermission: "service_beneficiaries:read",
    navigation: {
      label: "Ayants droit",
      icon: Users,
      parent: "users",
    },
    search: {
      value: "beneficiaries-page",
      label: "Ayants droit",
      category: "Principales",
      keywords: ["ayant", "droit", "ayants", "droits", "bénéficiaires", "famille"],
    },
  },

  externalContacts: {
    path: EXTERNAL_CONTACTS_PAGE_PATH,
    feature: "service_external_contacts",
    readPermission: "service_external_contacts:read",
    navigation: {
      label: "Contacts",
      icon: Contact,
      parent: "users",
    },
    search: {
      value: "external-contacts",
      label: "Contacts",
      category: "Principales",
      keywords: ["contact"],
    },
  },

  groups: {
    path: GROUPS_PAGE_PATH,
    feature: {
      feature: "service_usergroups",
      requiresAny: ["service_representatives", "service_employees", "service_external_contacts"],
    },
    readPermission: "service_usergroups:read",
    navigation: {
      label: "Groupes",
      icon: Users2,
      parent: "users",
    },
    search: {
      value: "groups",
      label: "Groupes",
      category: "Principales",
      keywords: ["groupe"],
    },
  },

  // Page combinée création/édition d'un groupe + gestion de ses membres. Même
  // feature gate que `groups` (gère des membres des 3 features). Pas d'entrée
  // `navigation`/`search` — accessible via la table et le bouton « Ajouter ».
  groupNew: {
    path: GROUP_NEW_PATH,
    feature: {
      feature: "service_usergroups",
      requiresAny: ["service_representatives", "service_employees", "service_external_contacts"],
    },
    readPermission: "service_usergroups:read",
  },

  groupEdit: {
    path: GROUP_EDIT_PATH,
    feature: {
      feature: "service_usergroups",
      requiresAny: ["service_representatives", "service_employees", "service_external_contacts"],
    },
    readPermission: "service_usergroups:read",
  },

  // Dynamic group builder. Without these, findRouteByPath fell through to the
  // `/groupes` entry — it produced the right gate, but only by prefix accident: renaming
  // or restructuring that entry would have silently left these routes ungated, with no
  // compile-time or test signal (same reasoning as yeatiLocNewProduct below).
  // The base entry also protects edit URLs beneath this builder; the generic
  // /groupes/:groupId route must not capture these deeper paths.
  dynamicGroupBuilder: {
    path: DYNAMIC_GROUP_BUILDER_BASE,
    feature: {
      feature: "service_usergroups",
      requiresAll: ["service_dynamic_group"],
      requiresAny: ["service_representatives", "service_employees", "service_external_contacts"],
    },
    readPermission: "service_usergroups:read",
  },

  dynamicGroupNew: {
    path: DYNAMIC_GROUP_NEW_PATH,
    feature: {
      feature: "service_usergroups",
      requiresAll: ["service_dynamic_group"],
      requiresAny: ["service_representatives", "service_employees", "service_external_contacts"],
    },
    readPermission: "service_usergroups:read",
  },

  proofDocuments: {
    path: PROOF_DOCUMENTS_PAGE_PATH,
    feature: { any: ["service_proof_document", "service_proof_document_legacy"] },
    readPermission: "service_proof_document:read",
    upsellCategory: "proof_documents",
    navigation: {
      label: "Justificatifs",
      icon: FileCheck,
      order: 30,
    },
    search: {
      value: "proof-documents",
      label: "Justificatifs",
      category: "Principales",
      keywords: ["justificatif", "document", "preuve"],
    },
  },

  accountingMovements: {
    path: ACCOUNTING_MOVEMENTS_PAGE_PATH,
    feature: "service_compta",
    readPermission: "service_compta:read",
    navigation: {
      label: "Rapprochement bancaire",
      icon: Landmark,
      parent: "accounting",
    },
    search: {
      value: "accounting-movements",
      label: "Rapprochement bancaire",
      category: "Comptabilité",
      keywords: ["compta", "comptabilite", "mouvements", "ecriture", "grand livre", "affectation"],
    },
  },

  accountingSettlements: {
    path: ACCOUNTING_SETTLEMENTS_PAGE_PATH,
    feature: "service_compta",
    readPermission: "service_compta:read",
    navigation: {
      label: "Remises bancaires",
      icon: CreditCard,
      parent: "accounting",
    },
    search: {
      value: "accounting-settlements",
      label: "Remises bancaires",
      category: "Comptabilité",
      keywords: ["compta", "cheque", "reglement", "non remis", "prelevement", "CB", "règlement"],
    },
  },

  accountingLedger: {
    path: ACCOUNTING_LEDGER_PAGE_PATH,
    feature: "service_compta",
    readPermission: "service_compta:read",
    navigation: {
      label: "Grand livre",
      icon: Landmark,
      parent: "accounting",
    },
    search: {
      value: "accounting-ledger",
      label: "Grand livre",
      category: "Comptabilité",
      keywords: ["compta", "grand", "livre", "affectation", "affectations"],
    },
  },

  accountingStock: {
    path: ACCOUNTING_STOCK_PAGE_PATH,
    feature: "service_compta",
    readPermission: "service_compta:read",
    navigation: {
      label: "Stock",
      icon: Package,
      parent: "accounting",
    },
    search: {
      value: "accounting-stock",
      label: "Grand livre des stocks",
      category: "Comptabilité",
      keywords: ["compta", "stock", "stocks", "grand livre", "inventaire", "famille", "référence", "export"],
    },
  },

  accountingSynthesis: {
    path: ACCOUNTING_SYNTHESIS_PAGE_PATH,
    feature: "service_compta",
    readPermission: "service_compta:read",
    navigation: {
      label: "Synthèse",
      icon: BarChart3,
      parent: "accounting",
    },
    search: {
      value: "accounting-synthesis",
      label: "Synthèse comptable",
      category: "Comptabilité",
      keywords: ["compta", "synthese", "synthèse", "bilan", "résumé", "export"],
    },
  },

  accountingBudget: {
    path: ACCOUNTING_BUDGET_PAGE_PATH,
    feature: "service_compta",
    readPermission: "service_compta:read",
    navigation: {
      label: "Budgets",
      icon: Wallet,
      parent: "accounting",
    },
    search: {
      value: "accounting-budget",
      label: "Budgets comptables",
      category: "Comptabilité",
      keywords: ["budget", "prévisionnel", "exercice", "millésime", "catégories", "delta"],
    },
  },

  voucherCampaign: {
    path: VOUCHER_CAMPAIGN_PATH,
    feature: "service_vouchers",
    readPermission: "service_vouchers:read",
    navigation: {
      label: "Émettre des titres",
      icon: Gift,
      parent: "vouchers",
    },
    search: {
      value: "events",
      label: "Émettre des titres",
      category: "Titres cadeaux",
      keywords: ["evenement", "events", "campagne", "titre"],
    },
  },

  voucherEmployees: {
    path: VOUCHER_EMPLOYEES_PATH,
    feature: "service_vouchers",
    readPermission: "service_vouchers:read",
    navigation: {
      label: "Titres crées",
      icon: TicketPlus,
      parent: "vouchers",
    },
    search: {
      value: "voucher-employees",
      label: "Titres créés",
      category: "Titres cadeaux",
      keywords: ["titre", "cadeau"],
    },
  },

  voucherTopUps: {
    path: VOUCHER_TOP_UPS_PATH,
    feature: "service_vouchers",
    readPermission: "service_vouchers:read",
    navigation: {
      label: "Flux créditeur CSE",
      icon: TrendingUp,
      parent: "vouchers",
    },
    search: {
      value: "voucher-top-ups",
      label: "Flux créditeur CSE",
      category: "Titres cadeaux",
      keywords: ["flux", "crediteur", "cse", "credit"],
    },
  },

  voucherBalance: {
    path: VOUCHER_BALANCE_PATH,
    feature: "service_vouchers",
    readPermission: "service_vouchers:read",
    navigation: {
      label: "Bons de commande",
      icon: Euro,
      parent: "vouchers",
    },
    search: {
      value: "voucher-balance",
      label: "Bon de commande",
      category: "Titres cadeaux",
      keywords: ["solde", "bon", "commande"],
    },
  },

  cardReissuance: {
    path: CARD_REISSUANCE_PATH,
    feature: "service_vouchers",
    readPermission: "service_card:read",
    navigation: {
      label: "Réédition de carte",
      icon: CreditCard,
      parent: "vouchers",
    },
    search: {
      value: "card-reissuance",
      label: "Réédition de carte",
      category: "Titres cadeaux",
      keywords: ["reedition", "carte", "bancaire"],
    },
  },

  ecomOrders: {
    path: ECOM_ORDERS_PAGE_PATH,
    feature: "service_deals",
    readPermission: "service_deals:read",
    navigation: {
      label: "Bons Plans",
      icon: ShoppingBag,
      parent: "deals",
    },
    search: {
      value: "ecom-orders",
      label: "Bons Plans",
      category: "Bons Plans",
      keywords: ["bons plans", "e-commerce", "ecom", "commande", "demande", "cse", "achats"],
    },
  },

  refundRequests: {
    path: REFUND_REQUESTS_PAGE_PATH,
    feature: "service_refund",
    readPermission: "service_refund:read",
    navigation: {
      label: "Remboursements",
      icon: Receipt,
      parent: "refunds",
    },
    search: {
      value: "refund-requests",
      label: "Remboursements",
      category: "Remboursement",
      keywords: ["remboursement", "remboursements", "demande", "refund"],
    },
  },

  checkCampaigns: {
    path: CHECK_CAMPAIGNS_PATH,
    feature: "service_checks",
    navigation: {
      label: "Émettre des titres",
      icon: Gift,
      parent: "checks",
    },
    search: {
      value: "check-campaigns",
      label: "Émettre des titres cadeaux",
      category: "Cartes cadeaux",
      keywords: ["carte", "cadeau", "campagne", "cheque", "famille", "emettre", "titre"],
    },
  },

  checkEmployees: {
    path: CHECK_EMPLOYEES_PATH,
    feature: "service_checks",
    navigation: {
      label: "Titre cadeau démat.",
      icon: TicketPlus,
      parent: "checks",
    },
    search: {
      value: "check-employees",
      label: "Titre cadeau démat.",
      category: "Cartes cadeaux",
      keywords: ["carte", "emise", "cadeau", "salarie", "titre", "demat"],
    },
  },

  checkContributions: {
    path: CHECK_CONTRIBUTIONS_PATH,
    feature: "service_checks",
    navigation: {
      label: "Flux créditeur",
      icon: TrendingUp,
      parent: "checks",
    },
    search: {
      value: "check-contributions",
      label: "Flux créditeur cartes cadeaux",
      category: "Cartes cadeaux",
      keywords: ["abondement", "credit", "flux", "facture", "crediteur"],
    },
  },

  jobs: {
    path: JOBS_PAGE_PATH,
    feature: "service_jobs",
    readPermission: "service_jobs:read",
    navigation: {
      label: "Tâches",
      icon: ListTodo,
      parent: "tools",
    },
    search: {
      value: "jobs",
      label: "Tâches",
      category: "Principales",
      keywords: ["tache", "tâche"],
    },
  },

  openQuestions: {
    path: OPEN_QUESTIONS_PAGE_PATH,
    feature: "service_openquestions",
    readPermission: "service_openquestions:read",
    navigation: {
      label: "Assistant IA",
      icon: MessageCircle,
      parent: "tools",
    },
    search: {
      value: "assistant",
      label: "Assistant IA",
      category: "Principales",
      keywords: ["assistant", "ia", "ai", "chatbot", "question", "openquestions", "gpt"],
    },
  },

  legalQuestions: {
    path: LEGAL_QUESTIONS_PAGE_PATH,
    feature: "service_legal",
    readPermission: "service_legal:read",
    navigation: {
      label: "Questions juridiques",
      icon: Scale,
      parent: "tools",
    },
    search: {
      value: "legal",
      label: "Questions juridiques",
      category: "Principales",
      keywords: ["juridique", "legal", "droit", "question"],
    },
  },

  polls: {
    path: POLLS_PAGE_PATH,
    feature: "service_polls",
    readPermission: "service_polls:read",
    navigation: {
      label: "Sondages",
      icon: BarChart3,
      parent: "activities",
    },
    search: {
      value: "polls",
      label: "Sondages",
      category: "Principales",
      keywords: ["sondage", "vote"],
    },
  },

  surveys: {
    path: SURVEYS_PAGE_PATH,
    feature: "service_surveys",
    readPermission: "service_surveys:read",
    navigation: {
      label: "Enquêtes",
      icon: ClipboardList,
      parent: "activities",
    },
    search: {
      value: "surveys",
      label: "Enquêtes",
      category: "Principales",
      keywords: ["enquete", "enquête", "questionnaire", "survey"],
    },
  },

  pinnedNotes: {
    path: PINNED_NOTES_PATH,
    feature: "service_pinnednotes",
    readPermission: "service_pinnednotes:read",
    navigation: {
      label: "Notes épinglées",
      icon: StickyNote,
      parent: "communication",
    },
    search: {
      value: "pinned-notes",
      label: "Notes épinglées",
      category: "Principales",
      keywords: ["note", "epinglee", "pense-bete", "memo", "pinned", "post-it"],
    },
  },

  taskManager: {
    path: TASK_MANAGER_PAGE_PATH,
    feature: "service_task_manager",
    readPermission: "service_task_manager:read",
    navigation: {
      label: "Gestionnaire de projet",
      icon: KanbanSquare,
      parent: "tools",
    },
    search: {
      value: "task-manager",
      label: "Gestionnaire de projet",
      category: "Principales",
      keywords: ["projet", "tache", "kanban", "gestionnaire", "todo", "board"],
    },
  },

  discussions: {
    path: DISCUSSIONS_PAGE_PATH,
    feature: "service_exchanges",
    readPermission: "service_exchanges:read",
    navigation: {
      label: "Discussions",
      icon: MessageSquare,
      parent: "communication",
    },
    search: {
      value: "discussions",
      label: "Discussions",
      category: "Principales",
      keywords: ["discussion", "message"],
    },
  },

  chat: {
    path: CHAT_PAGE_PATH,
    feature: "service_chat",
    readPermission: "service_chat:read",
    navigation: {
      label: "Chat",
      icon: MessageCircle,
      parent: "communication",
    },
    search: {
      value: "chat",
      label: "Chat",
      category: "Communication",
      keywords: ["chat", "messagerie", "conversation"],
    },
  },

  auditLogs: {
    path: AUDIT_LOGS_PAGE_PATH,
    feature: "service_audit_logs",
    readPermission: "service_audit_logs:read",
    navigation: {
      label: "Historique",
      icon: History,
      parent: "tools",
    },
    search: {
      value: "audit-logs",
      label: "Historique",
      category: "Principales",
      keywords: ["historique", "audit", "logs", "journal", "traces"],
    },
  },

  communicationInbox: {
    path: COMMUNICATION_DISCUSSIONS_PATH,
    feature: "service_communication",
    readPermission: "service_communication:read",
    navigation: {
      label: "Emails",
      icon: Mail,
      parent: "communication",
      activePaths: [COMMUNICATION_REPLIES_PATH],
    },
    search: {
      value: "communication-inbox",
      label: "Boîte de réception",
      category: "Communication",
      keywords: ["email", "message", "inbox", "boite", "reception", "conversation"],
    },
  },

  communicationNewsletters: {
    path: COMMUNICATION_NEWSLETTERS_PATH,
    feature: "service_communication",
    readPermission: "service_newsletter:read",
    navigation: {
      label: "Newsletters",
      icon: Mails,
      parent: "communication",
    },
    search: {
      value: "communication-newsletters",
      label: "Newsletters",
      category: "Communication",
      keywords: ["newsletter", "campagne", "emailing", "modele", "template", "merge", "champ"],
    },
  },

  communicationSavedReplies: {
    path: COMMUNICATION_REPLIES_PATH,
    feature: "service_communication",
    readPermission: "service_communication:read",
    search: {
      value: "communication-saved-replies",
      label: "Réponses enregistrées",
      category: "Communication",
      keywords: ["reponses", "enregistrées", "email", "réponses", "réutilisables"],
    },
  },

  communicationCseMessages: {
    path: COMMUNICATION_CSE_MESSAGES_PATH,
    feature: "service_cse_messages",
    readPermission: "service_cse_messages:read",
    navigation: {
      label: "Messages d'accueil",
      icon: MessageSquareText,
      parent: "communication",
    },
    search: {
      value: "communication-cse-messages",
      label: "Messages d'accueil",
      category: "Communication",
      keywords: ["message", "accueil", "pop-up", "modal", "bannière", "toaster"],
    },
  },

  refundsConfiguration: {
    path: REFUNDS_CONFIGURATION_PATH,
    feature: "service_refund",
    readPermission: "service_refund:read",
    navigation: {
      label: "Configuration",
      icon: Settings,
      parent: "refunds",
    },
    search: {
      value: "refund-configuration",
      label: "Configuration Remboursement",
      category: "Remboursement",
      keywords: ["remboursement", "configuration", "familles", "références"],
    },
  },

  visio: {
    path: VISIO_PAGE_PATH,
    feature: "service_visio",
    readPermission: "service_visio:read",
    navigation: {
      label: "Visioconférence",
      icon: Video,
      parent: "communication",
    },
    search: {
      value: "visio",
      label: "Visioconférence",
      category: "Principales",
      keywords: ["visio", "visioconference", "video", "reunion", "jitsi", "salon"],
    },
  },

  // Visio opened in its own tab: dedicated entries so the gate does not depend on the
  // `/visio` prefix (same reasoning as dynamicGroupBuilder).
  visioPublicStandalone: {
    path: VISIO_PUBLIC_STANDALONE_PATH,
    feature: "service_visio",
    readPermission: "service_visio:read",
  },

  visioRoomStandalone: {
    path: VISIO_ROOM_STANDALONE_PATH,
    feature: "service_visio",
    readPermission: "service_visio:read",
  },

  pushNotifications: {
    path: PUSH_NOTIFICATIONS_PAGE_PATH,
    feature: "service_push",
    readPermission: "service_push:read",
    navigation: {
      label: "Notifications push",
      icon: Bell,
      parent: "communication",
    },
    search: {
      value: "push-notifications",
      label: "Notifications push",
      category: "Principales",
      keywords: ["notification", "push", "message", "alerte"],
    },
  },

  smsAdmin: {
    path: SMS_ADMIN_PAGE_PATH,
    feature: "service_sms",
    readPermission: "service_sms:read",
    navigation: {
      label: "Envois SMS",
      icon: MessageCircle,
      parent: "communication",
    },
    search: {
      value: "sms",
      label: "Envois SMS",
      category: "Principales",
      keywords: ["sms", "envoi", "message", "texte"],
    },
  },

  changelogs: {
    path: CHANGELOGS_PAGE_PATH,
    feature: "service_changelog",
    search: {
      value: "changelogs",
      label: "Mises à jour",
      category: "Principales",
      keywords: ["nouveautés", "changelog", "mise à jour", "mises à jour", "release", "version"],
    },
  },

  fileManager: {
    path: FILE_MANAGER_PATH,
    feature: "service_drive",
    readPermission: "service_drive:read",
    upsellCategory: "drive",
    navigation: {
      label: "Gestionnaire de fichiers",
      icon: FolderOpen,
      order: 90,
    },
    search: {
      value: "file-manager",
      label: "Gestionnaire de fichiers",
      category: "Principales",
      keywords: ["fichier", "document", "gestionnaire", "dossier", "pdf", "excel", "word", "image", "jpg", "jpeg", "png", "gif", "svg"],
    },
  },

  blog: {
    path: BLOG_PAGE_PATH,
    feature: "service_blog",
    readPermission: "service_blog:read",
    navigation: {
      label: "Blog",
      icon: Newspaper,
      parent: "communication",
    },
    search: {
      value: "blog",
      label: "Blog",
      category: "Principales",
      keywords: ["blog", "article", "actualité", "posts"],
    },
  },

  calendar: {
    path: CALENDAR_PATH,
    feature: "service_calendar",
    readPermission: "service_calendar:read",
    navigation: {
      label: "Calendrier",
      icon: Calendar,
      parent: "activities",
    },
    search: {
      value: "calendar",
      label: "Calendrier",
      category: "Principales",
      keywords: ["calendrier", "agenda", "événement", "date", "organisation", "organiser"],
    },
  },

  tombola: {
    path: TOMBOLA_PAGE_PATH,
    feature: "service_lottery",
    readPermission: "service_lottery:read",
    navigation: {
      label: "Tombola",
      icon: Ticket,
      parent: "activities",
    },
    search: {
      value: "tombola",
      label: "Tombola",
      category: "Principales",
      keywords: ["tombola", "loterie", "tirage"],
    },
  },

  adventCalendar: {
    path: ADVENT_CALENDAR_PAGE_PATH,
    feature: "service_advent_calendar",
    readPermission: "service_advent_calendar:read",
    navigation: {
      label: "Calendrier des jeux",
      icon: CalendarDays,
      parent: "activities",
    },
    search: {
      value: "advent-calendar",
      label: "Calendrier des jeux",
      category: "Principales",
      keywords: ["calendrier des jeux", "jeux", "avent", "calendrier de l'avent", "noël", "cadeaux", "lots", "tombola"],
    },
  },

  arcade: {
    path: ARCADE_PAGE_PATH,
    feature: "service_yeati_arcade",
    readPermission: "service_yeati_arcade:read",
    navigation: {
      label: "Yeati Arcade",
      icon: Gamepad2,
      parent: "activities",
    },
    search: {
      value: "arcade",
      label: "Yeati Arcade",
      category: "Principales",
      keywords: ["arcade", "arena", "mini-jeux", "jeux", "jeu", "compétition", "classement", "lots", "freeplay", "runner"],
    },
  },

  pronostics: {
    path: PRONOSTICS_PAGE_PATH,
    requiresRepresentativeOrganization: true,
    navigation: {
      label: "Yeati Prono",
      icon: Trophy,
      parent: "activities",
    },
    search: {
      value: "pronostics",
      label: "Yeati Prono",
      category: "Principales",
      keywords: ["pronostic", "pronostics", "yeati prono", "classement", "matchs", "compétition", "lots"],
    },
  },

  // Competition management retains its feature and permission gates; only the
  // catalog is available to prospects.
  pronosticCompetitionDetail: {
    path: PRONOSTIC_COMPETITION_DETAIL_PATH,
    feature: "service_pronostic",
    readPermission: "service_pronostic:read",
  },

  deals: {
    path: DEALS_PAGE_PATH,
    feature: "service_deals",
    readPermission: "service_deals:read",
    navigation: {
      label: "Catalogues & Produits",
      icon: Tag,
      parent: "deals",
    },
    search: {
      value: "deals",
      label: "Bons Plans",
      category: "Principales",
      keywords: ["bons", "plans", "deals", "catalogue", "famille", "reference", "billetterie"],
    },
  },

  deliveryModes: {
    path: DELIVERY_MODES_PAGE_PATH,
    feature: "service_deals",
    readPermission: "service_deals:read",
    navigation: {
      label: "Modes de livraison",
      icon: Truck,
      parent: "deals",
    },
    search: {
      value: "delivery-modes",
      label: "Modes de livraison",
      category: "Principales",
      keywords: ["livraison", "delivery", "mode", "expedition", "bons", "plans"],
    },
  },

  suppliers: {
    path: SUPPLIERS_PAGE_PATH,
    search: {
      value: "suppliers",
      label: "Entreprises",
      category: "Principales",
      keywords: ["entreprise", "lieux", "bureau", "antennes"],
    },
  },

  userSettings: {
    path: USER_SETTINGS_PATH,
    feature: "service_preferences",
    search: {
      value: "user-settings",
      label: "Préférences utilisateur",
      category: "Préférences",
      keywords: ["utilisateur", "preferences", "paramètres", "réglages", "avatar"],
    },
  },

  parameterIdentiteCse: {
    path: PARAMETER_IDENTITE_CSE_PATH,
    search: {
      value: "identite-du-cse",
      label: "Identité du CSE",
      category: "Paramètres",
      keywords: ["identite", "cse", "personnalisation", "personnaliser", "logo", "image", "fond", "baseline", "accueil"],
    },
  },

  studio: {
    path: PARAMETER_STUDIO_PATH,
    feature: "service_theme",
    readPermission: "service_theme:read",
    search: {
      value: "studio",
      label: "Yeati Studio",
      category: "Paramètres",
      keywords: ["studio", "theme", "apparence", "design", "couleur", "banniere", "menu", "dashboard", "tableau", "application mobile"],
    },
  },

  parameterCoordonnees: {
    path: PARAMETER_COORDONNEES_PATH,
    feature: "organization_parameters",
    readPermission: "organization_parameters:read",
    search: {
      value: "coordonnees",
      label: "Coordonnées",
      category: "Paramètres",
      keywords: ["coordonnees", "adresse", "adresse de livraison", "signataires", "livraison", "par défaut"],
    },
  },

  parameterInformations: {
    path: PARAMETER_INFORMATIONS_PATH,
    feature: "organization_parameters",
    readPermission: "organization_parameters:read",
    search: {
      value: "informations",
      label: "Informations",
      category: "Paramètres",
      keywords: ["informations", "info", "details", "ouverture", "election", "mandat"],
    },
  },

  parameterSites: {
    path: PARAMETER_SITES_PATH,
    feature: "organization_parameters",
    readPermission: "organization_parameters:read",
    search: {
      value: "sites",
      label: "Gestion des sites",
      category: "Paramètres",
      keywords: ["sites", "site", "gestion", "adresse", "adresses", "adresse de livraison"],
    },
  },

  parameterDataManagement: {
    path: PARAMETER_DATA_MANAGEMENT_PATH,
    feature: "service_custom_attribute",
    readPermission: "service_custom_attribute:read",
    search: {
      value: "data-management",
      label: "Gestion des données",
      category: "Paramètres",
      keywords: ["gestion", "donnees", "data", "champs", "attributs", "personnalises", "management"],
    },
  },

  parameterBeneficiaries: {
    path: PARAMETER_BENEFICIARIES_PATH,
    feature: "service_beneficiaries",
    readPermission: "service_beneficiaries:read",
    search: {
      value: "beneficiaries",
      label: "Ayants droit",
      category: "Paramètres",
      keywords: ["ayants", "droits", "beneficiaires", "beneficiaries", "famille"],
    },
  },

  parameterWelcomeEmail: {
    path: PARAMETER_WELCOME_EMAIL_PATH,
    feature: "organization_parameters",
    readPermission: "organization_parameters:read",
    search: {
      value: "welcome-email",
      label: "E-Mail de bienvenue",
      category: "Paramètres",
      keywords: ["email", "mail", "courriel", "bienvenue"],
    },
  },

  parameterProofDocuments: {
    path: PARAMETER_PROOF_DOCUMENTS_PATH,
    feature: { any: ["service_proof_document", "service_proof_document_legacy"] },
    readPermission: "service_proof_document:read",
    search: {
      value: "proof-documents-settings",
      label: "Gestion des justificatifs",
      category: "Paramètres",
      keywords: ["justificatifs", "documents", "gestion", "parametres"],
    },
  },

  parameterPermissions: {
    path: PARAMETER_REPRESENTATIVE_ROLES_PATH,
    feature: "service_representative_roles",
    readPermission: "service_representative_roles:read",
    search: {
      value: "permissions-settings",
      label: "Droits d'accès",
      category: "Paramètres",
      keywords: ["droits", "acces", "permissions", "roles", "role"],
    },
  },

  parameterDashboardLayouts: {
    path: PARAMETER_DASHBOARD_LAYOUTS_PATH,
    feature: "service_dashboard_layouts",
    search: {
      value: "dashboard-layouts",
      label: "Tableaux de bord",
      category: "Paramètres",
      keywords: ["tableau", "bord", "dashboard", "layout", "widget"],
    },
  },

  parameterNavPosition: {
    path: PARAMETER_NAV_POSITION_PATH,
    feature: "service_navigation_layout",
    search: {
      value: "nav-position",
      label: "Position du menu",
      category: "Paramètres",
      keywords: ["navigation", "menu", "position", "sidebar", "navbar", "parametres"],
    },
  },

  parameterEmailSignature: {
    path: PARAMETER_EMAIL_SIGNATURE_PATH,
    feature: "service_communication",
    readPermission: "service_communication:read",
    search: {
      value: "email-signature",
      label: "Signature d'email du CSE",
      category: "Paramètres",
      keywords: ["signature", "email", "mail", "courriel"],
    },
  },

  userSettingsSignature: {
    path: `${USER_SETTINGS_PATH}/signature`,
    feature: "service_communication",
    readPermission: "service_communication:read",
    search: {
      value: "user-signature-email",
      label: "Signature d'email utilisateur",
      category: "Préférences",
      keywords: ["signature", "email", "mail", "courriel", "signature email", "utilisateur", "préférences"],
    },
  },

  parameters: {
    path: PARAMETER_PATH,
    navigation: {
      label: "Paramètres CSE",
      icon: Settings,
      order: 1000,
    },
  },

  rules: {
    path: RULES_PAGE_PATH,
    feature: "rules_engine",
    readPermission: "rules_engine:read",
    search: {
      value: "rules",
      label: "Règles",
      category: "Paramètres",
      keywords: ["règles", "automatisation", "rule", "barème"],
    },
  },

  customForms: {
    path: CUSTOM_FORMS_PAGE_PATH,
    feature: "custom_forms",
    readPermission: "custom_forms:read",
    navigation: {
      label: "Formulaires",
      icon: ClipboardList,
      parent: "communication",
      order: 115,
    },
    search: {
      value: "custom-forms",
      label: "Formulaires",
      category: "Principales",
      keywords: ["formulaires", "formulaire", "campagne", "collecte", "qf", "quotient"],
    },
  },

  subsidyPolicies: {
    path: SUBSIDY_POLICIES_PAGE_PATH,
    feature: "service_subsidies_rules_engine",
    readPermission: "service_subsidies_rules_engine:read",
    navigation: {
      label: "Politiques",
      icon: Wallet,
      parent: "subsidies",
      order: 35,
    },
    search: {
      value: "subsidy-policies",
      label: "Politiques de subventions",
      category: "Subventions",
      keywords: ["subvention", "subventions", "politique", "règle", "plafond", "budget", "enveloppe"],
    },
  },
  subsidyLedger: {
    path: SUBSIDY_LEDGER_PAGE_PATH,
    feature: "service_subsidies_rules_engine",
    readPermission: "service_subsidies_rules_engine:read",
    navigation: {
      label: "Utilisation",
      icon: ReceiptText,
      parent: "subsidies",
    },
    search: {
      value: "subsidy-ledger",
      label: "Utilisation des subventions",
      category: "Subventions",
      keywords: ["écriture", "écritures", "ledger", "consommation", "subvention"],
    },
  },
  subsidyCredits: {
    path: SUBSIDY_CREDITS_PAGE_PATH,
    feature: "service_subsidies_rules_engine",
    readPermission: "service_subsidies_rules_engine:read",
    navigation: {
      label: "Crédits",
      icon: Euro,
      parent: "subsidies",
    },
    search: {
      value: "subsidy-credits",
      label: "Crédits de subventions",
      category: "Subventions",
      keywords: ["crédit", "crédits", "approvisionnement", "paiement", "subvention"],
    },
  },
  activityOperations: {
    path: ACTIVITY_OPERATIONS_PAGE_PATH,
    feature: "service_compta",
    readPermission: "service_compta:read",
    navigation: {
      label: "Activités",
      icon: ClipboardList,
      parent: "accounting",
    },
    search: {
      value: "activity-operations",
      label: "Activités par salarié",
      category: "Comptabilité",
      keywords: ["activité", "activités", "avoir", "facture", "location", "solde", "débit", "crédit", "perceuse"],
    },
  },

  // YTI-1926 : dernier du menu Comptabilité.
  accountingSettings: {
    path: ACCOUNTING_SETTINGS_PAGE_PATH,
    feature: "service_compta",
    readPermission: "service_compta:read",
    navigation: {
      label: "Réglages",
      icon: Settings,
      parent: "accounting",
    },
    search: {
      value: "accounting-settings",
      label: "Réglages comptabilité",
      category: "Comptabilité",
      keywords: ["réglages", "paramètres", "comptes", "bridge", "synchronisation", "iban", "bic", "bancaire"],
    },
  },

  yeatiLocDashboard: {
    path: YEATI_LOC_PATH,
    feature: "service_catalog",
    navigation: {
      label: "Tableau de bord",
      icon: BarChart3,
      parent: "yeatiLoc",
    },
    search: {
      value: "yeati-loc-dashboard",
      label: "Location - Tableau de bord",
      category: "Location",
      keywords: ["location", "loc", "tableau", "bord", "dashboard", "yeati loc"],
    },
  },

  yeatiLocCatalogue: {
    path: YEATI_LOC_CATALOGUE_PATH,
    feature: "service_catalog",
    navigation: {
      label: "Catalogue",
      icon: Package,
      parent: "yeatiLoc",
    },
    search: {
      value: "yeati-loc-catalogue",
      label: "Location - Catalogue",
      category: "Location",
      keywords: ["catalogue", "produits", "location", "hebergement", "materiel"],
    },
  },

  // Entrées explicites pour le wizard de création et la page détail d'un
  // produit. Avant on dépendait du prefix-match `findRouteByPath` sur
  // `/locations/catalogue/` — ça fonctionnait mais devenait fragile si
  // une future route `/locations/catalogue/xxx` non gated apparaissait.
  // Avec ces entrées dédiées, `FeatureProtectedRoute` lit `service_catalog`
  // directement sans heuristique.
  yeatiLocNewProduct: {
    path: YEATI_LOC_NEW_PRODUCT_PATH,
    feature: "service_catalog",
  },

  yeatiLocProductDetail: {
    path: YEATI_LOC_PRODUCT_DETAIL_PATH,
    feature: "service_catalog",
  },

  yeatiLocCollections: {
    path: YEATI_LOC_COLLECTIONS_PATH,
    feature: "service_catalog",
    navigation: {
      label: "Collections",
      icon: FolderOpen,
      parent: "yeatiLoc",
    },
    search: {
      value: "yeati-loc-collections",
      label: "Location - Collections",
      category: "Location",
      keywords: ["collections", "thèmes", "categories", "groupes", "location"],
    },
  },

  // La route reste accessible directement (lien externe, search palette,
  // etc.). Pas d'entrée `navigation:` ici : les items « Réservations
  // <collection> » sont injectés dynamiquement dans la sidebar par
  // `injectReservationsByCollection` (cf. Sidebar/index.tsx) à partir
  // de la liste des collections actives — un item statique unique aurait
  // créé un doublon ou caché les variantes par collection.
  yeatiLocReservations: {
    path: YEATI_LOC_RESERVATIONS_PATH,
    feature: "service_catalog",
    search: {
      value: "yeati-loc-reservations",
      label: "Location - Réservations",
      category: "Location",
      keywords: ["reservations", "booking", "location", "calendrier"],
    },
  },

  // Detail page (`/locations/reservations/:bookingId`) — référencée
  // pour la feature gate (sans cette entrée, `findRouteByPath`
  // retournait `undefined` et la guard tombait à `true` par défaut).
  yeatiLocBookingDetail: {
    path: YEATI_LOC_BOOKING_DETAIL_PATH,
    feature: "service_catalog",
  },

  yeatiLocPlanning: {
    path: YEATI_LOC_PLANNING_PATH,
    feature: "service_catalog",
    navigation: {
      label: "Planning",
      icon: GanttChart,
      parent: "yeatiLoc",
    },
    search: {
      value: "yeati-loc-planning",
      label: "Location - Planning",
      category: "Location",
      keywords: ["planning", "calendrier", "timeline", "gantt", "disponibilite"],
    },
  },

  yeatiLocSettings: {
    path: YEATI_LOC_SETTINGS_PATH,
    feature: "service_catalog",
    navigation: {
      label: "Paramètres",
      icon: Settings,
      parent: "yeatiLoc",
    },
    search: {
      value: "yeati-loc-settings",
      label: "Location - Paramètres",
      category: "Location",
      keywords: ["parametres", "settings", "configuration", "equipements", "amenities"],
    },
  },

  classifiedAds: {
    path: CLASSIFIED_ADS_PATH,
    feature: "service_classifieds",
    readPermission: "service_classifieds:read",
    upsellCategory: "classifieds",
    navigation: {
      label: "Petites Annonces",
      icon: Handshake,
      order: 47,
    },
    search: {
      value: "classified-ads",
      label: "Petites Annonces",
      category: "Principales",
      keywords: ["petites", "annonces", "classified", "ads", "modération", "moderation", "catégories"],
    },
  },
};

export const checkFeatureRequirement = (
  requirement: RouteFeatureRequirement | undefined,
  features: Partial<PlanFeatures>,
): boolean => {
  if (!requirement) {
    return true;
  }

  if (typeof requirement === "string") {
    return isEffectiveFeatureEnabled(requirement, features);
  }

  if ("all" in requirement) {
    return requirement.all.every((feature) => isEffectiveFeatureEnabled(feature, features));
  }

  if ("any" in requirement) {
    return requirement.any.some((feature) => isEffectiveFeatureEnabled(feature, features));
  }

  if ("feature" in requirement && "requiresAny" in requirement) {
    return (
      isEffectiveFeatureEnabled(requirement.feature, features) &&
      (requirement.requiresAll ?? []).every((feature) => isEffectiveFeatureEnabled(feature, features)) &&
      requirement.requiresAny.some((feature) => isEffectiveFeatureEnabled(feature, features))
    );
  }

  return false;
};

export const getAccessibleRoutes = (features: Partial<PlanFeatures>): RouteConfig[] => {
  return Object.values(ROUTE_REGISTRY).filter((route) =>
    checkFeatureRequirement(route.feature, features),
  );
};

export const isRouteAccessible = (
  path: string,
  features: Partial<PlanFeatures>,
): boolean => {
  const route = Object.values(ROUTE_REGISTRY).find((r) => r.path === path);
  if (!route) {
    return true;
  }
  return checkFeatureRequirement(route.feature, features);
};

export const checkRouteReadPermission = (
  route: RouteConfig,
  permissions: RepresentativePermissions | null,
): boolean => {
  if (!route.readPermission) return true;
  if (!permissions) return true;
  return !!permissions[route.readPermission];
};

const ROUTE_BY_EXACT_PATH = new Map(
  Object.values(ROUTE_REGISTRY).map((route) => [route.path, route]),
);

const ROUTES_BY_PREFIX = Object.values(ROUTE_REGISTRY)
  .sort((a, b) => b.path.length - a.path.length);

export const findRouteByPath = (path: string): RouteConfig | undefined =>
  ROUTE_BY_EXACT_PATH.get(path) ??
  ROUTES_BY_PREFIX.find((route) => route.path.includes(":") && matchPath({ path: route.path, end: true }, path)) ??
  ROUTES_BY_PREFIX.find(
    (route) => path.startsWith(route.path) && path.charAt(route.path.length) === "/",
  );
